import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _toString from 'lodash/toString';
import _isEqual from 'lodash/isEqual';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
// Core components
import AppLoader from 'components/core/AppLoader';
import Popper from 'components/core/Popper';
import TextField from 'components/core/TextField';
import TablePaginationNavigation from 'components/core/table/TablePaginationNavigation';
// Custom
import { invoke } from 'utils/Tools';
import { DisabledContext } from 'components/misc/Contexts';
import { CloseIconButton, SearchIconButton } from 'components/buttons/IconButtons';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';

class AutoCompleteSearchFieldFilter extends React.Component {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleSelectElement = this.handleSelectElement.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);

    this.performChange = _debounce(this.doPerformInputChange, 500);

    this.searchField = React.createRef();

    const { element, elementDescBuilder } = this.props;
    this.state = {
      value: element ? elementDescBuilder(element) : '',
      selectedElement: element,
      resultsAnchorEl: null,
      resultsOpen: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { loading, element, elementDescBuilder } = this.props;
    const { loading: prevLoading, element: prevElement } = prevProps;
    // Check if new elements have been loaded
    if (prevLoading && !loading) {
      this.doDisplayElements();
    }
    // Check if an element has been set by parent
    if (!_isEqual(prevElement, element)) {
      invoke(() => {
        this.setState({
          value: element ? elementDescBuilder(element) : '',
          selectedElement: element,
        });
      });
    }
  }

  // Handlers
  handleChange(value, currentTarget) {
    this.setState({
      value,
      resultsAnchorEl: currentTarget,
    }, () => this.performChange(value));
  }

  handleClose() {
    this.setState({
      resultsOpen: false,
    });
  }

  handleSelectElement(element) {
    const { elementDescBuilder, onSelectElement } = this.props;
    this.setState({
      value: elementDescBuilder(element),
      selectedElement: element,
      resultsOpen: false,
    }, () => {
      onSelectElement(element);
    });
  }

  handleSearch() {
    this.doGetList();
  }

  handleCancel() {
    const { onSelectElement } = this.props;
    this.setState({
      value: '',
      selectedElement: null,
      resultsOpen: false,
    }, () => {
      onSelectElement(null);
    });
  }

  handlePageChange(page) {
    this.doGetList(page);
  }

  // Private
  doPerformInputChange(value) {
    if (!_isEmpty(value)) {
      this.doGetList();
    }
  }

  doGetList(page) {
    const { value } = this.state;
    const { onGetElements } = this.props;
    // Get elements list
    onGetElements(value, page);
  }

  doDisplayElements() {
    // Display Popper
    this.setState({
      resultsOpen: true,
    }, () => {
      // Give focus to search field
      // Does not work all the time without setTimeout...
      setTimeout(() => this.searchField.current.focus(), 100);
    });
  }

  render() {
    const {
      label, loading, elements, pagination, elementComp,
    } = this.props;
    const {
      value, selectedElement, resultsAnchorEl, resultsOpen,
    } = this.state;

    const elementSelected = !_isNil(selectedElement);
    const nonEmpty = !elementSelected && !_isEmpty(value);
    // Display end adornment
    let endAdornment;
    if (loading) {
      endAdornment = <div style={{ padding: '12px' }}><AppLoader size={20} /></div>;
    } else if (elementSelected) {
      endAdornment = <CloseIconButton onClick={this.handleCancel} tooltipId="tooltip.delete" />;
    } else if (nonEmpty) {
      endAdornment = <SearchIconButton onClick={this.handleSearch} />;
    }

    return (
      <DisabledContext.Consumer>
        {(disabled) => (
          <React.Fragment>
            <TextField
              ref={this.searchField}
              disabled={disabled}
              label={<LocalizedMessage id={label} />}
              value={value}
              adornments={{ end: endAdornment }}
              onChange={this.handleChange}
            />
            <Popper
              open={resultsOpen}
              anchorEl={resultsAnchorEl}
              placement="bottom-start"
              onClose={this.handleClose}
            >
              <div style={{ minWidth: '300px', maxHeight: '300px', overflowY: 'auto' }}>
                <LoadingDisplayer loading={loading}>
                  {pagination && pagination.totalPages > 1 && (
                    <div className="p-2 d-flex justify-content-center">
                      <TablePaginationNavigation
                        currentPage={pagination.pageNumber}
                        totalPages={pagination.totalPages}
                        onChange={this.handlePageChange}
                      />
                    </div>
                  )}
                  {resultsOpen && !_isEmpty(elements) ? (
                    _map(elements, (element) => (
                      <div key={_toString(element.id)}>
                        <elementComp.type
                          {...elementComp.props}
                          element={element}
                          onSelect={this.handleSelectElement}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="p-3">
                      <em><LocalizedMessage id="info.listNoElement" /></em>
                    </div>
                  )}
                  {pagination && pagination.totalPages > 1 && (
                    <div className="p-2 d-flex justify-content-center">
                      <TablePaginationNavigation
                        currentPage={pagination.pageNumber}
                        totalPages={pagination.totalPages}
                        onChange={this.handlePageChange}
                      />
                    </div>
                  )}
                </LoadingDisplayer>
              </div>
            </Popper>
          </React.Fragment>
        )}
      </DisabledContext.Consumer>
    );
  }
}

AutoCompleteSearchFieldFilter.propTypes = {
  label: PropTypes.string.isRequired,
  element: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  loading: PropTypes.bool.isRequired,
  elements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  pagination: PropTypes.shape({
    pageNumber: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
  }),
  onGetElements: PropTypes.func.isRequired,
  elementComp: PropTypes.element.isRequired,
  elementDescBuilder: PropTypes.func.isRequired,
  onSelectElement: PropTypes.func.isRequired,
};

AutoCompleteSearchFieldFilter.defaultProps = {
  element: null,
  pagination: null,
};

module.exports = AutoCompleteSearchFieldFilter;



// WEBPACK FOOTER //
// ./components/filters/AutoCompleteSearchFieldFilter.js